import { Form, FormInstance, Input, Typography } from "antd-v5";
import { CVSS_FIELDS_NEW_UI } from "constants/CVSSConstants";
import useUpdateEffect from "hooks/useUpdateEffect";
import { isNil, keys } from "lodash";
import React, {
  ChangeEventHandler,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  cvssVectorValidator,
  extractCVSSVersion,
  getCvssScoreFromVector,
  parseCVSSVectorStringToFields,
  validateCVSSVectorFormat,
} from "utils/cvssUtil";

import { NOT_AVAILABLE } from "ac-components/ac-na-component/ac-na-component";

import CVSSFormFields from "./CVSSFormFields";

const { Text } = Typography;

const StandardVersionFields = ({
  version,
  initialCvssVector,
  onUpdate,
  onCompletenessChange,
  form,
  viewMode,
  resetKey,
}: {
  initialCvssVector: string;
  onCompletenessChange: (complete: boolean) => void;
  onUpdate: (data: {
    vector: string;
    score: number | null | undefined;
  }) => void;
  version: string;
  form: FormInstance;
  viewMode?: boolean;
  resetKey: number;
}) => {
  const [cvssObject, setCvssObject] = useState(() => {
    if (extractCVSSVersion(initialCvssVector) === version) {
      return parseCVSSVectorStringToFields(initialCvssVector);
    }
    return {};
  });
  const [_form] = Form.useForm(form);

  useEffect(() => {
    // If the new version tab matches the originally saved vector, populate the form with it.
    if (extractCVSSVersion(initialCvssVector) === version) {
      setCvssObject(parseCVSSVectorStringToFields(initialCvssVector));
    } else {
      // If it's a different version, start with a completely empty form.
      setCvssObject({});
    }
  }, [version, initialCvssVector]);

  useUpdateEffect(() => {
    setCvssObject({});
  }, [resetKey]);

  const adaptedFields = useMemo(() => {
    const sourceData =
      CVSS_FIELDS_NEW_UI[version as keyof typeof CVSS_FIELDS_NEW_UI];

    if (!sourceData) return [];
    return sourceData.map((field) => ({
      ...field,
      // For CVSS 4.0, use its 'group' property as the main category
      category:
        //@ts-expect-error  Property 'group' does not exist on type '{ name: string; label: string; options: { label: string; value: string; }[]; colSpan: number; }'.
        field.category ? field.category : version === "4.0" ? field.group : "",
      //@ts-expect-error Property 'subCategory' does not exist on type '{ name: string; label: string; options: { label: string; value: string; }[]; colSpan: number; }'
      subCategory: field.subCategory || "", // generic sub-category
    }));
  }, [version]);

  const { cvssVector, cvssScore, isVectorComplete } = useMemo(() => {
    const requiredFieldCount = adaptedFields.length;
    const selectedFieldCount = keys(cvssObject).length;
    const isComplete =
      version !== "None" &&
      requiredFieldCount > 0 &&
      selectedFieldCount === requiredFieldCount;

    if (!isComplete) {
      return { cvssVector: "", cvssScore: null, isVectorComplete: false };
    }

    const metricsString = adaptedFields
      .map((field) => {
        const key = field.name.replace(/\d+$/, ""); // 'AV20' -> 'AV'
        const value = cvssObject[field.name];
        return `${key}:${value}`;
      })
      .join("/");
    const vector = `CVSS:${version}/${metricsString}`;
    const score = getCvssScoreFromVector(vector);
    return { cvssVector: vector, cvssScore: score, isVectorComplete: true };
  }, [cvssObject, version, adaptedFields]);

  useUpdateEffect(() => {
    onUpdate({ vector: cvssVector, score: cvssScore });
    onCompletenessChange(isVectorComplete);
  }, [cvssVector, cvssScore, isVectorComplete, onUpdate, onCompletenessChange]);

  useEffect(() => {
    form.setFieldsValue({ manualVector: cvssVector });
  }, [cvssVector, form]);

  const handleCvssChange = useCallback((value, fieldName) => {
    setCvssObject((prevState) => ({
      ...prevState,
      [fieldName]: value,
    }));
  }, []);

  const onManualVectorChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    const manualVector = e.target.value;
    if (
      validateCVSSVectorFormat(manualVector) &&
      extractCVSSVersion(manualVector) === version
    ) {
      const newCvssObject = parseCVSSVectorStringToFields(manualVector);
      setCvssObject(newCvssObject);
    } else if (manualVector === "") {
      setCvssObject({});
    }
  };

  return (
    <>
      <CVSSFormFields
        showHeader={false}
        fields={adaptedFields}
        cvssObject={cvssObject}
        handleCvssChange={handleCvssChange}
        includeNotDefined={false}
        subtitle=""
        viewMode={viewMode}
      />
      <div className="cvss-metrics-wrapper">
        <div>
          <Text strong>CVSS Vector </Text>
          <Form
            form={_form}
            initialValues={{ manualVector: cvssVector }}
            disabled={viewMode}
          >
            <Form.Item
              name="manualVector"
              rules={[
                {
                  validator: (_rule, value) => {
                    if (!value) {
                      return Promise.resolve();
                    }
                    if (validateCVSSVectorFormat(value)) {
                      const _version = value ? extractCVSSVersion(value) : null;
                      if (version !== _version) {
                        return Promise.reject(`Vector is not CVSS ${version}`);
                      }
                    }
                    return Promise.resolve();
                  },
                },
                {
                  ...cvssVectorValidator(),
                },
              ]}
            >
              <Input onChange={onManualVectorChange} />
            </Form.Item>
          </Form>
        </div>
        <div>
          <Text strong>Base Score: </Text>
          <Text>{isNil(cvssScore) ? NOT_AVAILABLE : cvssScore}</Text>
        </div>
      </div>
    </>
  );
};
export default StandardVersionFields;
